export type ToolModule = {
  render: (root: HTMLElement) => void | Promise<void>;
};

export type ToolMeta = {
  id: string;
  title: string;
  description: string;
  category: 'pages' | 'optimize' | 'convert' | 'edit' | 'security';
  icon: string;
  load: () => Promise<ToolModule>;
  beta?: boolean;
};

export const tools: ToolMeta[] = [
  {
    id: 'compress',
    title: 'Compress PDF',
    description: 'Reduce PDF file size for email, forms and sharing.',
    category: 'optimize',
    icon: '↓',
    load: () => import('./compress/compress'),
  },
  {
    id: 'organize',
    title: 'Organize PDF',
    description: 'Reorder, rotate, remove, extract and add pages.',
    category: 'pages',
    icon: '▤',
    load: () => import('./organize/organize'),
  },
  {
    id: 'merge',
    title: 'Merge PDF',
    description: 'Combine multiple PDF files into one document.',
    category: 'pages',
    icon: '+',
    load: () => import('./merge/merge'),
  },
  {
    id: 'pdf-to-docx',
    title: 'PDF → Word',
    description: 'Create DOCX from text-based PDFs with basic formatting.',
    category: 'convert',
    icon: 'W',
    load: () => import('./pdf-to-docx/pdf-to-docx'),
  },
  {
    id: 'sign',
    title: 'Sign PDF',
    description: 'Add a visual signature, text, date or check mark.',
    category: 'edit',
    icon: 'S',
    load: () => import('./sign/sign'),
  },
  {
    id: 'jpg-to-pdf',
    title: 'JPG / PNG → PDF',
    description: 'Turn images and photos into a single PDF.',
    category: 'convert',
    icon: '▧',
    load: () => import('./jpg-to-pdf/jpg-to-pdf'),
  },
  {
    id: 'edit',
    title: 'PDF Editor',
    description: 'Add annotations, images and beta text-layer edits.',
    category: 'edit',
    icon: '✎',
    beta: true,
    load: () => import('./edit/edit'),
  },
  {
    id: 'split',
    title: 'Split PDF',
    description: 'Extract pages or split a PDF into separate files.',
    category: 'pages',
    icon: '⌁',
    load: () => import('./split/split'),
  },
  {
    id: 'rotate',
    title: 'Rotate PDF',
    description: 'Rotate selected pages by 90, 180 or 270 degrees.',
    category: 'pages',
    icon: '↻',
    load: () => import('./rotate/rotate'),
  },
  {
    id: 'pdf-to-jpg',
    title: 'PDF → JPG',
    description: 'Export PDF pages as JPG or PNG images.',
    category: 'convert',
    icon: '▣',
    load: () => import('./pdf-to-jpg/pdf-to-jpg'),
  },
  {
    id: 'pdf-to-txt',
    title: 'PDF → Text',
    description: 'Extract selectable text from a PDF file.',
    category: 'convert',
    icon: 'T',
    load: () => import('./pdf-to-txt/pdf-to-txt'),
  },
  {
    id: 'docx-to-pdf',
    title: 'Word → PDF',
    description: 'Convert DOCX documents to PDF in your browser.',
    category: 'convert',
    icon: 'D',
    load: () => import('./docx-to-pdf/docx-to-pdf'),
  },
  {
    id: 'xlsx-to-pdf',
    title: 'Excel → PDF',
    description: 'Convert XLSX spreadsheets into printable PDFs.',
    category: 'convert',
    icon: 'X',
    load: () => import('./xlsx-to-pdf/xlsx-to-pdf'),
  },
  {
    id: 'csv-to-pdf',
    title: 'CSV → PDF',
    description: 'Turn CSV or TSV data into a clean PDF report.',
    category: 'convert',
    icon: 'C',
    load: () => import('./csv-to-pdf/csv-to-pdf'),
  },
];

export const categoryLabels: Record<ToolMeta['category'], string> = {
  pages: 'Pages',
  optimize: 'Optimize',
  convert: 'Convert',
  edit: 'Edit',
  security: 'Security',
};

export function findTool(id: string): ToolMeta | undefined {
  return tools.find((t) => t.id === id);
}
